import { StreamCoordinatesGet, StreamCoordinates } from "./interface";
import { Layout } from "./Layout";
import { assert, toDiv } from "../../utils";

export class GridLayout implements Layout {
  constructor(private readonly config: Config) {}

  streamCoordinatesGet(
    streamCoordinatesGet: StreamCoordinatesGet
  ): StreamCoordinates {
    const { streamMargin } = this.config;
    const { x, y, width, height } =
      this.initialStreamCoordinatesGet(streamCoordinatesGet);

    const aspect = width / height;
    const width1 = toDiv(width - 2 * streamMargin, 8);
    const height1 = toDiv(width1 / aspect, 8);

    const padWidth = Math.floor((aspect * height1) / 2) * 2;
    const padX = Math.floor((width - padWidth) / 4) * 2;

    return {
      x: x + padX,
      y: y + (height - height1) / 2,
      width: padWidth,
      height: height1
    };
  }

  private initialStreamCoordinatesGet(
    streamCoordinatesGet: StreamCoordinatesGet
  ): StreamCoordinates {
    const { width: width0, height: height0 } = this.config;
    const { streamIndex, streamsCount } = streamCoordinatesGet;

    switch (streamsCount) {
      case 1: {
        assert(
          [0].includes(streamIndex),
          `Invalid ${streamCoordinatesGet} data`
        );

        return {
          width: width0,
          height: height0,
          x: 0,
          y: 0
        };
      }
      case 2: {
        assert(
          [0, 1].includes(streamIndex),
          `Invalid ${streamCoordinatesGet} data`
        );

        const width1 = width0 / 2;
        const height1 = height0 / 2;

        return {
          width: width1,
          height: height1,
          x: streamIndex * width1,
          y: height1 / 2
        };
      }
      case 3: {
        assert(
          [0, 1, 2].includes(streamIndex),
          `Invalid ${streamCoordinatesGet} data`
        );

        const width1 = width0 / 2;
        const height1 = height0 / 2;

        return {
          width: width1,
          height: height1,
          x: [2].includes(streamIndex) ? width1 / 2 : streamIndex * width1,
          y: [2].includes(streamIndex) ? height1 : 0
        };
      }
      case 4: {
        assert(
          [0, 1, 2, 3].includes(streamIndex),
          `Invalid ${streamCoordinatesGet} element`
        );

        const width1 = width0 / 2;
        const height1 = height0 / 2;

        return {
          width: width1,
          height: height1,
          x: [0, 2].includes(streamIndex) ? 0 : width1,
          y: Math.floor(streamIndex / 2) * height1
        };
      }
      case 5: {
        assert(
          [0, 1, 2, 3, 4].includes(streamIndex),
          `Invalid ${streamCoordinatesGet} element`
        );

        const width1 = width0 / 3;
        const height1 = height0 / 3;

        return {
          width: width1,
          height: height1,
          x: [3, 4].includes(streamIndex)
            ? (streamIndex % 3) * width1 + width1 / 2
            : (streamIndex % 3) * width1,
          y: [0, 1, 2].includes(streamIndex)
            ? height1 / 2
            : height1 + height1 / 2
        };
      }
      case 6: {
        assert(
          [0, 1, 2, 3, 4, 5].includes(streamIndex),
          `Invalid ${streamCoordinatesGet} element`
        );

        const width1 = width0 / 3;
        const height1 = height0 / 3;

        return {
          width: width1,
          height: height1,
          x: (streamIndex % 3) * width1,
          y: [0, 1, 2].includes(streamIndex)
            ? height1 / 2
            : height1 + height1 / 2
        };
      }
      case 7: {
        assert(
          [0, 1, 2, 3, 4, 5, 6].includes(streamIndex),
          `Invalid ${streamCoordinatesGet} element`
        );

        const width1 = width0 / 3;
        const height1 = height0 / 3;

        return {
          width: width1,
          height: height1,
          x: [6].includes(streamIndex) ? width1 : (streamIndex % 3) * width1,
          y: Math.floor(streamIndex / 3) * height1
        };
      }
      case 8: {
        assert(
          [0, 1, 2, 3, 4, 5, 6, 7].includes(streamIndex),
          `Invalid ${streamCoordinatesGet} element`
        );

        const width1 = width0 / 3;
        const height1 = height0 / 3;

        return {
          width: width1,
          height: height1,
          x: [6, 7].includes(streamIndex)
            ? (streamIndex % 3) * width1 + width1 / 2
            : (streamIndex % 3) * width1,
          y: Math.floor(streamIndex / 3) * height1
        };
      }
      case 9: {
        assert(
          [0, 1, 2, 3, 4, 5, 6, 7, 8].includes(streamIndex),
          `Invalid ${streamCoordinatesGet} element`
        );

        const width1 = width0 / 3;
        const height1 = height0 / 3;

        return {
          width: width1,
          height: height1,
          x: (streamIndex % 3) * width1,
          y: Math.floor(streamIndex / 3) * height1
        };
      }
      default:
        assert(false, `Invalid streamsCount ${streamsCount}`);
    }
  }
}

interface Config {
  width: number;
  height: number;
  streamMargin: number;
}
